/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import axios from "axios";

/**
 * Which backend this is depends on `REACT_APP_API_BASE_URL`, set in a `.env` file.
 *
 * `npm start` resolves that value and prints it before CRA boots
 * (scripts/dev-server.js), so which backend you are on is never a guess:
 *   npm start              dev-api.vecros.com
 *   npm run start:local    a local API on 127.0.0.1:8787
 *   npm run api:which      report the target without starting anything
 */
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8787";

/**
 * Single axios instance for every vecros-api call.
 *
 * Auth is the VCS_SESSION httpOnly cookie, so `withCredentials` is mandatory and
 * there is no Authorization header to attach — the browser carries the session.
 * Nothing auth-related is readable from JS by design.
 */
const client = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

/**
 * vecros-api returns errors as `{detail: <string>}`, except the auth/MFA routes
 * which nest an object: `{detail: {code, message}}`. Pydantic validation (422)
 * returns an array. Flatten all three into a predictable Error.
 */
export class ApiError extends Error {
  constructor(message, { status, code, detail } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

const parseError = (error) => {
  if (!error.response) {
    return new ApiError(
      "Cannot reach the server. Check your connection and try again.",
      { code: "NetworkError" }
    );
  }

  const { status, data } = error.response;
  const detail = data?.detail;

  if (typeof detail === "string") {
    return new ApiError(detail, { status, detail });
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return new ApiError(detail.message || detail.code || "Request failed.", {
      status,
      code: detail.code,
      detail,
    });
  }
  if (Array.isArray(detail)) {
    // FastAPI 422: [{loc: [...], msg, type}]
    const first = detail[0];
    const field = first?.loc?.filter((p) => p !== "body").join(".");
    return new ApiError(
      [field, first?.msg].filter(Boolean).join(": ") || "Invalid request.",
      { status, code: "ValidationError", detail }
    );
  }
  return new ApiError(`Request failed (${status}).`, { status, detail });
};

/**
 * A 401 on any call means the session cookie expired. Try `/auth/refresh` once
 * (it rotates the session server-side) and replay the original request.
 *
 * Concurrent 401s share one refresh so a page issuing several requests does not
 * fire several refreshes and revoke its own freshly-rotated session.
 */
let refreshPromise = null;
let onSessionExpired = () => {};

export const setSessionExpiredHandler = (handler) => {
  onSessionExpired = handler;
};

// `/auth/me` is the bootstrap probe: a 401 there means "not signed in", not
// "session expired", so refreshing would just add a doomed round-trip to every
// anonymous page load.
const NO_RETRY_PATHS = ["/auth/login", "/auth/login/mfa", "/auth/refresh", "/auth/me"];

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const isAuthRoute = NO_RETRY_PATHS.some((p) => original?.url?.startsWith(p));

    if (status !== 401 || isAuthRoute || original?._retried) {
      return Promise.reject(parseError(error));
    }

    original._retried = true;
    try {
      if (!refreshPromise) {
        refreshPromise = client
          .post("/auth/refresh")
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
      return client(original);
    } catch (refreshError) {
      onSessionExpired();
      return Promise.reject(parseError(error));
    }
  }
);

export default client;
